'use client';

import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { shouldShowInstallHint, type InstallPlatform } from '@/lib/pwa-install';

const DISMISS_KEY = 'pwa.installHintDismissed';

type BIPEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };

function detectPlatform(): InstallPlatform {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua) && /Safari/.test(ua) && !/CriOS|FxiOS/.test(ua)) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'other';
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (window.navigator as { standalone?: boolean }).standalone === true
  );
}

export default function InstallHint() {
  const [platform, setPlatform] = useState<InstallPlatform>('other');
  const [standalone, setStandalone] = useState(true); // assume installed until checked (no flash)
  const [dismissed, setDismissed] = useState(true);
  const [prompt, setPrompt] = useState<BIPEvent | null>(null);
  const pathname = usePathname();

  useEffect(() => {
    setPlatform(detectPlatform());
    setStandalone(isStandalone());
    try {
      setDismissed(localStorage.getItem(DISMISS_KEY) === '1');
    } catch {
      setDismissed(false);
    }
    const onBIP = (e: Event) => {
      e.preventDefault();
      setPrompt(e as BIPEvent);
    };
    window.addEventListener('beforeinstallprompt', onBIP);
    return () => window.removeEventListener('beforeinstallprompt', onBIP);
  }, []);

  const { show, mode } = shouldShowInstallHint({
    platform,
    isStandalone: standalone,
    dismissed,
    canPrompt: !!prompt,
    pathname,
  });
  if (!show) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  const install = async () => {
    if (!prompt) return;
    await prompt.prompt();
    await prompt.userChoice.catch(() => undefined);
    dismiss();
  };

  // role="status", not "dialog": it is a passive tip, and comfy.css hides the
  // Comfy launcher whenever any [role=dialog] exists — the tip made Comfy
  // vanish until it was dismissed. .pwa-install-hint (globals.css) parks it
  // above the Comfy pill (and the phone "+") and hides it under any open
  // dialog or sheet, e.g. the patient peek whose buttons it covered.
  return (
    <div
      role="status"
      aria-label="Instalar Réhaut"
      className="pwa-install-hint"
      style={{
        position: 'fixed',
        left: 12,
        right: 12,
        zIndex: 999, // under the modal layer (1000); the :has rule hides it too
        margin: '0 auto',
        maxWidth: 460,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 14px',
        background: 'var(--surface, #fff)',
        border: '1px solid var(--hairline, rgba(0,0,0,0.1))',
        borderRadius: 14,
        boxShadow: '0 8px 28px rgba(40,30,70,0.18)',
        fontFamily: 'var(--font-sans)',
      }}
    >
      <span style={{ flex: 1, fontSize: 13, color: 'var(--ink, #2b2620)', lineHeight: 1.4 }}>
        {mode === 'ios' ? (
          <>
            Instala Réhaut: toca <strong>•••</strong> → <strong>Compartir</strong> → <strong>Agregar a inicio</strong>.
          </>
        ) : (
          <>Instala Réhaut como app para acceso rápido.</>
        )}
      </span>
      {mode === 'android' && (
        <button
          type="button"
          onClick={install}
          style={{
            flexShrink: 0,
            padding: '8px 14px',
            fontSize: 13,
            fontWeight: 600,
            color: '#fff',
            background: 'var(--accent-ink)',
            border: 'none',
            borderRadius: 10,
            cursor: 'pointer',
          }}
        >
          Instalar
        </button>
      )}
      <button
        type="button"
        onClick={dismiss}
        aria-label="Cerrar"
        style={{
          flexShrink: 0,
          width: 44,
          height: 44,
          margin: '-8px -10px -8px 0',
          fontSize: 16,
          color: 'var(--ink-3, #6b6256)',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        ✕
      </button>
    </div>
  );
}
